import { useEffect } from "react";
import { Link } from "react-router-dom";
import { FaBullseye, FaAward, FaHandshake } from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import RoadmapTimeline from "../components/RoadmapTimeline";

const WhoWeAre = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  const accreditations = [
    {
      name: "ISO 9001:2015",
      description: "Quality Management System Certified",
    },
    { name: "MSME Registered Institute", description: "Recognized Small & Medium Enterprise" },
    { name: "CEFR-Aligned Curriculum", description: "(A1–C2)" },
    { name: "Exam-Oriented Training", description: "DELF, DALF, TEF, TCF, Goethe" },
  ];

  const partners = [
    { name: "International Institute of Technology & Education (IITE)", description: "Academic Partnership" },
  ];

  return (
    <div className="min-h-screen bg-canvas pb-20 text-ink overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-primary/10 py-10 sm:py-12 md:py-14 lg:py-16">
        <div
          className="absolute inset-0 bg-cover bg-[center_15%]"
          style={{
            backgroundImage: `url('${process.env.PUBLIC_URL}/AboutUs1.png')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/35" />
        {/* <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-transparent to-primary/25" /> */}
        
        <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-4 sm:gap-6 px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/30 to-primary-soft/30 rounded-full blur-2xl animate-pulse" />
            <h1 className="relative text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-white pb-2 drop-shadow-lg">
              WELCOME TO The Lingua Alliance
            </h1>
          </div>
          <p className="max-w-3xl text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed text-white/90 drop-shadow-md">
            We believe every language opens the door to new opportunities and connections.
            From French to German and Spanish, we make language learning engaging, practical, and growth-driven—helping you explore the world with confidence.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary">
              About Us
            </span>
            <h2 className="mt-3 sm:mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold">
              Our Story
            </h2>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="space-y-4 sm:space-y-6 text-ink-muted leading-relaxed">
              <p className="text-sm sm:text-base lg:text-lg">
                At The Lingua Alliance, we are passionate about breaking down language barriers and opening doors to global opportunities. We specialize in teaching French, German, Spanish, and Japanese through structured, practical, and engaging programs designed for learners of all ages and backgrounds.
              </p>
              <p className="text-base sm:text-lg">
                Our approach combines expert instruction with immersive learning experiences that go beyond textbooks. We focus on real-world communication skills, cultural understanding, and exam preparation to ensure our students not only learn a language—they live it.
              </p>
              <p className="text-base sm:text-lg">
                Whether you're a student preparing for international exams, a professional seeking career advancement, or a lifelong learner exploring new cultures, The Lingua Alliance provides the guidance, resources, and support you need to succeed in your language journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Timeline Section */}
      <RoadmapTimeline />

      {/* Mission & Vision Section */}
      <section className="py-5 sm:py-8 md:py-10 bg-canvas">
          {/* Mission Statement - Standout Feature */}
          <div className="mt-8 sm:mt-12 md:mt-16 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-aos="zoom-in">
            <div className="relative rounded-2xl sm:rounded-3xl bg-gradient-to-br from-primary via-primary-soft to-primary p-[2px] shadow-[0_10px_40px_rgba(79,64,198,0.3)] sm:shadow-[0_20px_60px_rgba(79,64,198,0.4)]">
              <div className="rounded-2xl sm:rounded-3xl bg-white p-6 sm:p-8 md:p-12">
                <div className="text-center">
                  <div className="inline-flex h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary/10 to-primary-soft/10 text-2xl sm:text-3xl text-primary mb-4 sm:mb-6">
                    <FaBullseye />
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary mb-4 sm:mb-6 tracking-tight">
                    Our Mission
                  </h3>
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold text-ink leading-relaxed">
                    To empower learners with language skills that create{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary-soft">
                      global opportunities
                    </span>
                    , one conversation at a time.
                  </p>
                  <div className="mt-6 sm:mt-8 flex items-center justify-center gap-2">
                    <div className="h-1 w-8 sm:w-10 md:w-12 rounded-full bg-gradient-to-r from-primary to-primary-soft"></div>
                    <div className="h-1 w-8 sm:w-10 md:w-12 rounded-full bg-gradient-to-r from-primary-soft to-primary"></div>
                    <div className="h-1 w-8 sm:w-10 md:w-12 rounded-full bg-gradient-to-r from-primary to-primary-soft"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </section>

      {/* Team Section */}
      {/* <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          <div className="text-center mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              Our Team
            </span>
            <h2 className="mt-4 text-3xl font-semibold sm:text-4xl">
              Meet Our Experts
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-ink-muted">
              Our dedicated faculty and management team bring years of
              experience and passion for language education
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="group rounded-2xl border border-primary/10 bg-white p-6 shadow-elevated transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_30px_60px_rgba(18,10,56,0.18)]"
              >
                <div className="relative mb-4 aspect-square overflow-hidden rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  {member.placeholder && (
                    <div className="absolute inset-0 flex items-center justify-center bg-primary/5">
                      <span className="text-xs font-medium text-primary/60">
                        Photo TBD
                      </span>
                    </div>
                  )}
                </div>
                <h3 className="font-semibold text-ink mb-1">{member.name}</h3>
                <p className="text-sm text-primary font-medium mb-1">
                  {member.role}
                </p>
                <p className="text-xs text-ink-muted">{member.department}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Accreditations Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-canvas">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary">
              Accreditations
            </span>
            <h2 className="mt-3 sm:mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold">
              Recognized & Certified
            </h2>
            <p className="mt-3 sm:mt-4 max-w-2xl mx-auto text-sm sm:text-base text-ink-muted">
              Our certifications and partnerships reflect our commitment to
              excellence
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-8 sm:mb-10 md:mb-12">
            {accreditations.map((accreditation, index) => (
              <div
                key={index}
                className="group rounded-xl sm:rounded-2xl border border-primary/10 bg-white p-4 sm:p-6 shadow-elevated transition-all duration-300 hover:-translate-y-1 hover:shadow-float text-center"
              >
                <div className="inline-flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-lg sm:text-xl text-primary mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                  <FaAward />
                </div>
                <h3 className="font-semibold text-sm sm:text-base text-ink mb-2">
                  {accreditation.name}
                </h3>
                <p className="text-xs text-ink-muted">
                  {accreditation.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary">
              Our Partners
            </span>
            <h2 className="mt-3 sm:mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold">
              Trusted Partnerships
            </h2>
            <p className="mt-3 sm:mt-4 max-w-2xl mx-auto text-sm sm:text-base text-ink-muted">
              Collaborating with leading institutions and organizations
              worldwide
            </p>
          </div>

          <div className="flex items-center justify-center">
            {partners.map((partner, index) => (
              <div
                key={index}
                className="w-full max-w-[250px] sm:w-[250px] group rounded-xl sm:rounded-2xl border border-primary/10 bg-white p-4 sm:p-6 shadow-elevated transition-all duration-300 hover:-translate-y-1 hover:shadow-float text-center"
              >
                <div className="inline-flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-lg sm:text-xl text-primary mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                  <FaHandshake />
                </div>
                <h3 className="font-semibold text-ink mb-2">{partner.name}</h3>
                <p className="text-xs text-ink-muted">{partner.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-canvas">
        <div className="mx-auto max-w-4xl px-4 lg:px-6">
          <div className="rounded-3xl border border-primary/10 bg-gradient-to-br from-primary/5 via-white to-white/80 p-8 shadow-[0_28px_60px_rgba(15,23,42,0.18)]">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold mb-4">Join Our Team</h2>
              <p className="text-ink-muted">
                We're always looking for passionate educators and professionals
                to join our mission of building global communicators
              </p>
            </div>
            <Link
              to="/career"
              className="block w-full text-center rounded-full bg-gradient-to-r from-primary to-primary-soft px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)]"
            >
              Join Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhoWeAre;
